import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ChartData, ChartOptions } from 'chart.js';
import {ApexAxisChartSeries,ApexChart,ApexXAxis,ApexNonAxisChartSeries} from 'ng-apexcharts'
import { DashboardService, DashboardResponseDTO } from '../../services/dashboard.service';
import { LoadingService } from '../../services/loading.service';
import { ToastService } from '../../services/toast.service';
import { AuthorizationService } from '../../services/authorization.service';
import { WompiReportsService, DailyTaskStatusDTO } from '../../services/wompi-reports.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {

  // KPIs principales
  totalIncome = 0;
  totalExpenses = 0;
  activeParticipants = 0;
  companies = 0;
  netProfit = 0;
  pendingWorkOrders = 0;
  
  // Datos del dashboard completo
  dashboardData: DashboardResponseDTO | null = null;
  
  // Loading state
  isLoading = true;

  // Filtro de mes/año
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = new Date().getMonth() + 1; // 1-based
  availableYears: number[] = [];
  months = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
  ];

  // Nivel de acceso del usuario al dashboard
  accessLevel: 'BASIC' | 'FULL' | 'NONE' = 'NONE';
  
  // Estado de tarea diaria de reportes
  dailyTaskStatus: DailyTaskStatusDTO | null = null;
  isLoadingDailyTask = false;
  canViewReportUpload = false;
  
  /** Si el usuario tiene acceso completo al dashboard (KPIs, gráficos, etc.) */
  get hasFullAccess(): boolean {
    return this.accessLevel === 'FULL';
  }
  
  /** Si el usuario tiene al menos acceso básico al dashboard */
  get hasBasicAccess(): boolean {
    return this.accessLevel === 'BASIC' || this.accessLevel === 'FULL';
  }

  barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: []
  };

  barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          color: '#374151', // gray-700
          font: { weight: 'bold' }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#6B7280' // gray-500
        },
        grid: {
          color: '#E5E7EB' // gray-200
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#6B7280'
        },
        grid: {
          color: '#E5E7EB'
        }
      }
    }
  };

  doughnutChartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: []
  };

  doughnutChartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom'
      }
    },
    cutout: '70%',
  };

  topProjects: any[] = [];
  
  constructor(
    private dashboardService: DashboardService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private authorizationService: AuthorizationService,
    private wompiReportsService: WompiReportsService,
    private router: Router
  ) {}

  ngOnInit() {
    // Generar años disponibles (desde 2020 hasta el año actual)
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= 2020; y--) {
      this.availableYears.push(y);
    }
    this.loadDashboardData();
    this.checkReportUploadPermission();
  }
  
  checkReportUploadPermission() {
    this.canViewReportUpload = this.authorizationService.hasPermission('INTEGRATIONS_WOMPI_READ');
    if (this.canViewReportUpload) {
      this.loadDailyTaskStatus();
    }
  }
  
  loadDailyTaskStatus() {
    this.isLoadingDailyTask = true;
    this.wompiReportsService.getDailyTaskStatus().subscribe({
      next: (status) => {
        this.dailyTaskStatus = status;
        this.isLoadingDailyTask = false;
      },
      error: (error) => {
        console.error('Error cargando estado de tarea diaria:', error);
        this.isLoadingDailyTask = false;
      }
    });
  }
  
  goToReportUpload() {
    this.router.navigate(['/report-upload']);
  }

  onFilterChange() {
    this.loadDashboardData();
  }

  loadDashboardData() {
    this.isLoading = true;
    this.loadingService.show();

    this.dashboardService.getDashboardData(this.selectedYear, this.selectedMonth).subscribe({
      next: (response) => {
        console.log('✅ Dashboard data received:', response);
        this.dashboardData = response;
        this.accessLevel = response.accessLevel || 'BASIC';
        
        // Solo actualizar KPIs y gráficos si tiene acceso FULL
        if (this.hasFullAccess) {
          this.updateKpis(response);
          this.updateChartData(response);
          this.updateProjectsData(response);
        }
        
        this.isLoading = false;
        this.loadingService.hide();
        
        // Forzar detección de cambios para actualizar las gráficas
        this.cdr.detectChanges();
        console.log('🔄 Change detection triggered. Access Level:', this.accessLevel);
      },
      error: (error: any) => {
        console.error('❌ Error loading dashboard data:', error);
        
        // Si es error 403, el usuario no tiene permisos
        if (error.status === 403) {
          this.accessLevel = 'NONE';
          this.toastService.showWarning('No tienes permisos para acceder al dashboard');
        } else {
          this.toastService.showError('Error cargando los datos del dashboard');
        }
        
        this.isLoading = false;
        this.loadingService.hide();
      }
    });
  }

  private resetDashboardData() {
    // Reset KPIs
    this.totalIncome = 0;
    this.totalExpenses = 0;
    this.activeParticipants = 0;
    this.companies = 0;
    this.netProfit = 0;
    this.pendingWorkOrders = 0;
    
    // Reset chart data
    this.barSeries = [];
    this.barXAxis.categories = [];
    this.doughnutSeries = [];
    this.doughnutLabels = [];
    this.doughnutColors = [];
    
    // Reset projects
    this.topProjects = [];
    
    // Reset Chart.js data
    this.barChartData = {
      labels: [],
      datasets: []
    };
    this.doughnutChartData = {
      labels: [],
      datasets: []
    };
  }

  private updateKpis(data: DashboardResponseDTO) {
    this.totalIncome = data.kpis.totalIncome;
    this.totalExpenses = data.kpis.totalExpenses;
    this.activeParticipants = data.kpis.activeParticipants;
    this.companies = data.kpis.companies;
    this.netProfit = data.kpis.netProfit;
    this.pendingWorkOrders = data.kpis.pendingWorkOrders;
  }

  private updateChartData(data: DashboardResponseDTO) {
    console.log('📊 Updating charts with data:', data.charts);
    
    // Actualizar datos del gráfico de barras (Ingresos vs Egresos)
    const monthlyData = data.charts.monthlyIncomeVsExpenses;
    
    if (monthlyData && monthlyData.length > 0) {
      this.barSeries = [
        {
          name: 'Ingresos',
          data: monthlyData.map(m => m.income)
        },
        {
          name: 'Egresos', 
          data: monthlyData.map(m => m.expenses)
        }
      ];

      this.barXAxis = {
        categories: monthlyData.map(m => m.month.substring(0, 3))
      };
      
      console.log('📈 Bar series updated:', this.barSeries);
      console.log('📈 X-axis updated:', this.barXAxis);
    }

    // Actualizar datos del gráfico de dona (Estado de Cuotas)
    const paymentStatus = data.charts.paymentStatus;
    if (paymentStatus) {
      this.doughnutSeries = [paymentStatus.paid, paymentStatus.pending, paymentStatus.overdue];
      this.doughnutLabels = ['Pagadas', 'Pendientes', 'Vencidas'];
      this.doughnutColors = ['#3B82F6', '#F59E0B', '#EF4444'];
      
      console.log('🍩 Doughnut series updated:', this.doughnutSeries);
      console.log('🍩 Doughnut labels:', this.doughnutLabels);
    }

    // También actualizar los datos de Chart.js si es necesario
    if (monthlyData && monthlyData.length > 0) {
      this.barChartData = {
        labels: monthlyData.map(m => m.month.substring(0, 3)),
        datasets: [
          {
            type: 'bar',
            label: 'Ingresos',
            data: monthlyData.map(m => m.income),
            backgroundColor: '#3B82F6',
            borderRadius: 6,
          },
          {
            type: 'bar', 
            label: 'Egresos',
            data: monthlyData.map(m => m.expenses),
            backgroundColor: '#A855F7',
            borderRadius: 6,
          }
        ]
      };
    }

    if (paymentStatus) {
      this.doughnutChartData = {
        labels: ['Pagadas', 'Pendientes', 'Vencidas'],
        datasets: [
          {
            type: 'doughnut',
            data: [paymentStatus.paid, paymentStatus.pending, paymentStatus.overdue],
            backgroundColor: ['#3B82F6', '#F59E0B', '#EF4444'],
            borderWidth: 1,
            hoverOffset: 8,
          }
        ]
      };
    }
  }

  private updateProjectsData(data: DashboardResponseDTO) {
    if (data.projects && data.projects.topProfitableProjects) {
      this.topProjects = data.projects.topProfitableProjects.map(project => ({
        name: project.name,
        profit: project.profit
      }));
    } else {
      this.topProjects = [];
    }
    console.log('📈 Projects updated:', this.topProjects);
  }

  refreshDashboard() {
    this.loadDashboardData();
  }

  // TrackBy function para mejorar el rendimiento de *ngFor
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  // Ingresos vs Egresos
  barSeries: ApexAxisChartSeries = [];

  barChart: ApexChart = {
    type: 'bar',
    height: 350,
    toolbar: { show: false }
  };

  barXAxis: ApexXAxis = {
    categories: []
  };

  barColors: string[] = ['#3B82F6', '#A855F7']; // azul y púrpura

  // Estado de Cuotas
  doughnutSeries: ApexNonAxisChartSeries = [];
  doughnutLabels: string[] = [];
  doughnutColors: string[] = [];

  doughnutChart: ApexChart = {
    type: 'donut',
    height: 350
  };
}
